import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { ApiService } from '../../api.service';
import {
  CountryInfo,
  TalentEmploymentAuthorizationRequest,
  TalentMoatsAvailabilityV2,
  TalentMoatsOpportunity,
  TalentMoatsMoney,
  LookupFieldData,
  EeocFormDataV2
} from '../../../typings';

@Injectable({
  providedIn: 'root'
})
export class TalentProfileApiService {
  constructor(private apiService: ApiService) {
  }

  saveEmploymentOpportunity(preferences: TalentMoatsOpportunity, personId: Number): Observable<any> {
    return this.apiService.httpWrapper<void>({
      method: 'POST',
      url: `/api/profile/opportunity`,
      body: {
        opportunity: preferences,
        talentId: personId
      }
    });
  }

  getEmploymentOpportunity(personId: number): Observable<TalentMoatsOpportunity> {
      const params = new HttpParams();
      return this.apiService.httpWrapper<any>({
        method: 'GET',
        url: `/api/profile/opportunity/${personId}`,
        authenticated: true,
        params
      });
  }

  saveEmploymentAvailability(availability: TalentMoatsAvailabilityV2, personId: number): Observable<any> {
    return this.apiService.httpWrapper<void>({
      method: 'POST',
      url: `/api/profile/availability`,
      body: {
        availability: availability,
        talentId: personId
      }
    });
  }

  getEmploymentAvailability(personId: number): Observable<TalentMoatsAvailabilityV2> {
    const params = new HttpParams();
    return this.apiService.httpWrapper<any>({
      method: 'GET',
      url: `/api/profile/availability/${personId}`,
      authenticated: true,
      params
    });

  }

  saveEmploymentAuthorization(authorizationRequest: TalentEmploymentAuthorizationRequest): Observable<any> {
    return this.apiService.httpWrapper<void>({
      method: 'POST',
      url: `/api/profile/authorization`,
      body: JSON.stringify(authorizationRequest),
      headers: new HttpHeaders({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/json'
      })
    });

  }

  getLocaleId(personId: number) {
    const params = new HttpParams();
    return this.apiService.httpWrapper<any>({
      method: 'GET',
      url: `/api/profile/locale/${personId}`,
      authenticated: true,
      params
    });
  }

  getEmploymentSalary(personId: number): Observable<TalentMoatsMoney> {
    const params = new HttpParams();
    return this.apiService.httpWrapper<any>({
      method: 'GET',
      url: `/api/profile/salary/${personId}`,
      authenticated: true,
      params
    });
  }

  saveEmploymentSalary(salary: TalentMoatsMoney, personId: number): Observable<any> {
    return this.apiService.httpWrapper<void>({
      method: 'POST',
      url: `/api/profile/salary`,
      body: {
        money: salary,
        talentId: personId
      }
    });
  }

  getCurrencyCode(personId: number): Observable<string> {
    const params = new HttpParams();
    return this.apiService.httpWrapper<any>({
      method: 'GET',
      url: `/api/profile/currency/${personId}`,
      authenticated: true,
      params
    });
  }

  getEeocLookupFields(personId: number): Observable<Map<string, LookupFieldData[]>> {
    return this.apiService.httpWrapper<Map<string, LookupFieldData[]>>({
      method: 'GET',
      url: `/api/profile/lookupFields/${personId}`
    });
  }

  readEeocData(talentId: number): Observable<EeocFormDataV2> {
    return this.apiService.httpWrapper<EeocFormDataV2>({
      method: 'GET',
      url: `/api/profile/eeoc/${talentId}`,
      authenticated: true,
      withCredentials: true
    });
  }

  saveEeocData(request: EeocFormDataV2): Observable<any> {
    return this.apiService.httpWrapper<any>({
      method: 'POST',
      url: `/api/profile/save-eeoc`,
      body: request,
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    });
  }

  createEeocFormEntry(personId: number): Observable<any> {
    return this.apiService.httpWrapper<any>({
      method: 'POST',
      url: `/api/profile/create-eeoc-form/${personId}`,
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    });
  }

  createTalentProfileActivity(activityTypeId: number, personId: number, orderId: number, jobPostingId: number): Observable<void> {
    return this.apiService.httpWrapper<any>({
      method: 'POST',
      url: `/api/profile/activity`,
      body: {
        activityTypeId: activityTypeId,
        personId: personId,
        orderId: orderId,
        jobPostingId: jobPostingId
      }
    });
  }

  showProfileForTalent(personId: number): Observable<boolean> {
    return this.apiService.httpWrapper<any>({
      method: 'GET',
      url: `/api/profile/show-profile/${personId}`,
      authenticated: true
    });
  }

  talentProfileCompleted(personId: number): Observable<boolean> {
    return this.apiService.httpWrapper<any>({
      method: 'GET',
      url: `/api/profile/talent-profile-completed/${personId}`,
      authenticated: true
    });
  }

  sendDoThisLaterEmail(postingId: number, personId: number, talentName: string, talentLastName: string, talentEmail: string, jobTitle: string): Observable<any> {
    const params = new HttpParams()
      .set('postingId', postingId.toString())
      .set('talentName', talentName)
      .set('talentLastName', talentLastName)
      .set('personId', personId)
      .set('talentEmail', talentEmail)
      .set('jobTitle', jobTitle);
    return this.apiService.httpWrapper<any>({
      method: 'POST',
      url:`/api/quick-apply/send-do-this-later-email`,
      params
    });
  }

  sendGeneralApplicationDoThisLaterEmail(personId: number, talentName: string, talentLastName: string, talentEmail: string): Observable<any> {
    const params = new HttpParams()
      .set('talentName', talentName)
      .set('talentLastName', talentLastName)
      .set('personId', personId)
      .set('talentEmail', talentEmail)
    return this.apiService.httpWrapper<any>({
      method: 'POST',
      url:`/api/quick-apply/general-application/send-do-this-later-email`,
      params
    });
  }

}
